import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';

import { CurrentUserAndPermissions } from '®shared/current-user/current-user-and-permissions';
import { hasGrantRolePermissionForListId } from '®shared/permissions';
import { RoleId } from '®shared/permissions/roles';
import { UserRecord } from '®shared/user';

import { ListOwnerAndIsPublisher } from '../metadata/owner-with-list-publisher-flag';

import {
  ListOwnersEditorHideModalAction,
  ListOwnersEditorSaveAction,
} from './list-owners-editor.actions';
import { ListOwnersEditorState } from './list-owners.state';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'rl-list-owners-editor',
  templateUrl: './list-owners-editor.component.html',
})
export class ListOwnersEditorComponent implements OnChanges {
  @Input() currentUser: CurrentUserAndPermissions;
  @Input() listOwnersEditorState: ListOwnersEditorState;
  @Input() listOwners: UserRecord[];

  @Output() save = new EventEmitter<ListOwnersEditorSaveAction>();
  @Output() close = new EventEmitter<ListOwnersEditorHideModalAction>();

  listOwnersForm: UntypedFormGroup;
  saveWithPublisherRoleClicked = false;
  submitted = false;
  readonly MAX_OWNERS_COUNT = 10;

  constructor(private formBuilder: UntypedFormBuilder) {
    this.listOwnersForm = this.formBuilder.group({
      owners: this.formBuilder.array([], [this.validateMaxOwnersCount]),
    });
  }

  populateForm() {
    (this.listOwners || []).forEach((listOwner, index) => {
      this.listOwnersFormArray.setControl(
        index,
        new UntypedFormControl(listOwner),
      );
    });
  }

  ngOnChanges(changes?: SimpleChanges) {
    if (changes && changes.listOwners) {
      this.populateForm();
    }
  }

  get listOwnersFormArray(): UntypedFormArray {
    return this.listOwnersForm.get('owners') as UntypedFormArray;
  }

  get listOwnersFormArrayEmpty(): boolean {
    return this.listOwnersFormArray.value.length === 0;
  }

  get hasInviteListPublishersPermission(): boolean {
    return hasGrantRolePermissionForListId(
      RoleId.LIST_PUBLISHER,
      this.listOwnersEditorState.listId,
      this.currentUser.permissions,
    );
  }

  onCancel(): void {
    if (this.listOwnersEditorState.isSaving) {
      return;
    }

    this.close.emit(new ListOwnersEditorHideModalAction());
  }

  onSubmit(withPublisherRole: boolean): void {
    this.submitted = true;

    if (this.listOwnersEditorState.isSaving || this.listOwnersForm.invalid) {
      return;
    }

    this.saveWithPublisherRoleClicked = withPublisherRole;
    const ownersWithPublisherFlags: ListOwnerAndIsPublisher[] =
      this.listOwnersFormArray.value.map((owner: UserRecord) => {
        return { owner, isPublisher: withPublisherRole };
      });

    this.save.emit(
      new ListOwnersEditorSaveAction({
        listId: this.listOwnersEditorState.listId,
        ownersWithPublisherFlags,
      }),
    );
  }

  addListOwner(listOwner: UserRecord) {
    if (this.listOwnersEditorState.isSaving || this.isListOwner(listOwner)) {
      return;
    }

    this.listOwnersFormArray.push(new UntypedFormControl(listOwner));
  }

  removeListOwner(index: number) {
    if (this.listOwnersEditorState.isSaving) {
      return;
    }

    this.listOwnersFormArray.removeAt(index);
  }

  isListOwner(newOwner: UserRecord): boolean {
    return this.listOwnersFormArray.value.some(
      (owner: UserRecord) => owner.id === newOwner.id,
    );
  }

  validateMaxOwnersCount: ValidatorFn = (control: AbstractControl) => {
    if (control.value.length > this.MAX_OWNERS_COUNT) {
      return { tooManyOwners: true };
    }

    return null;
  };
}
